/**
 * 文件保存工具：优先调用系统"另存为"对话框（File System Access API）让用户选择保存位置，
 * 不支持的浏览器回退到传统下载。
 */

export interface SaveFileType {
  description: string;
  mimeType: string;
  extensions: string[];
}

export interface WritableFileStream {
  write: (data: Blob) => Promise<void>;
  close: () => Promise<void>;
}

export interface PickedFileHandle {
  name: string;
  createWritable: () => Promise<WritableFileStream>;
}

export const WORD_FILE_TYPE: SaveFileType = {
  description: 'Word 文档',
  mimeType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  extensions: ['docx'],
};

export const PDF_FILE_TYPE: SaveFileType = {
  description: 'PDF 文档',
  mimeType: 'application/pdf',
  extensions: ['pdf'],
};

/** 浏览器是否支持"另存为"对话框（Chrome/Edge 86+） */
export function supportsSavePicker(): boolean {
  return typeof window !== 'undefined' && typeof (window as any).showSaveFilePicker === 'function';
}

/**
 * 弹出系统"另存为"对话框让用户选择保存位置。
 * 必须在用户点击事件中调用；用户取消时返回 null。
 */
export async function pickSaveLocation(
  suggestedName: string,
  fileType: SaveFileType
): Promise<PickedFileHandle | null> {
  try {
    const handle = await (window as any).showSaveFilePicker({
      suggestedName,
      types: [
        {
          description: fileType.description,
          accept: { [fileType.mimeType]: fileType.extensions.map((ext) => `.${ext}`) },
        },
      ],
    });
    return handle as PickedFileHandle;
  } catch (err: any) {
    if (err?.name === 'AbortError') return null;
    throw err;
  }
}

/** 将内容写入用户选择的文件 */
export async function writeBlobToFile(handle: PickedFileHandle, blob: Blob): Promise<void> {
  const writable = await handle.createWritable();
  await writable.write(blob);
  await writable.close();
}

/** 回退方案：传统浏览器下载 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
}
